import { type FC } from "react";

import { useMutation } from "@apollo/client/react";

import { type Project, ProjectDestroyDocument } from "@app_schema";

import { DestroyDialog } from "@styled/destroy_dialog";

export const ProjectDestroyDialog: FC<{
  project: Pick<Project, "id" | "name">;
  onDestroy?(): void;
  onClose(): void;
}> = ({ project, onDestroy, onClose }) => {
  const [execute, { loading }] = useMutation(ProjectDestroyDocument, {
    // Evict the deleted project so it drops out of every cached list (e.g.
    // DashboardProjectsList). Delete often fires from the detail page where
    // the cache-first list isn't mounted, so a name-based refetch would
    // no-op; gc() prunes the now-dangling reference.
    update(cache) {
      cache.evict({
        id: cache.identify({ __typename: "Project", id: project.id }),
      });
      // `inboxStatusCounts` is a root field, not a Project, so evicting the
      // project leaves it stale: the rail keeps the old totals and the cases
      // list stays blank instead of showing the empty state once the last case
      // goes. Evicting it makes the cached list read incomplete, which refetches
      // entries, pagination and counts together.
      cache.evict({ id: "ROOT_QUERY", fieldName: "inboxStatusCounts" });
      cache.gc();
    },
  });

  const onConfirm = async () => {
    await execute({ variables: { id: project.id } });
    onClose();
    onDestroy?.();
  };

  return (
    <DestroyDialog
      name={project.name}
      onClose={onClose}
      onConfirm={onConfirm}
      loading={loading}
    >
      Deleting “{project.name}” may impact any agents using the case.
    </DestroyDialog>
  );
};
